import { useQuery } from '@tanstack/react-query';
import { useOrganization } from '@/contexts/OrganizationContext';
import { supabase } from '@/integrations/supabase/client';
import { SupportNotice, fromSupportNoticeRow } from '@/lib/supportNotices';

export const supportNoticesQueryKey = ['support-notices', 'all'] as const;
export const activeSupportNoticesQueryKey = ['support-notices', 'active'] as const;
export type SupportNoticePortalScope = 'workspace' | 'vendor';

async function fetchSupportNotices() {
  const { data, error } = await supabase
    .from('support_notices')
    .select('id, message, starts_at, ends_at, applies_to_all, audience_scope, created_at, support_notice_targets(organization_id, organizations(id, name))')
    .order('starts_at', { ascending: false });

  if (error) {
    throw error;
  }

  return (data ?? []).map(fromSupportNoticeRow);
}

async function fetchActiveSupportNoticesForOrg(
  organizationId: string,
  portal: SupportNoticePortalScope = 'workspace',
) {
  const { data, error } = await (supabase.rpc as any)('get_active_support_notices', {
    p_org_id: organizationId,
    p_portal: portal,
  });

  if (error) {
    throw error;
  }

  return (data ?? []).map(fromSupportNoticeRow);
}

export function useSupportNotices() {
  return useQuery<SupportNotice[]>({
    queryKey: supportNoticesQueryKey,
    queryFn: fetchSupportNotices,
    staleTime: 30_000,
  });
}

export function useActiveSupportNotices(portal: SupportNoticePortalScope = 'workspace') {
  const { organization } = useOrganization();

  return useQuery<SupportNotice[]>({
    queryKey: [...activeSupportNoticesQueryKey, portal, organization?.id ?? 'none'],
    queryFn: () => fetchActiveSupportNoticesForOrg(organization!.id, portal),
    enabled: !!organization?.id,
    staleTime: 30_000,
  });
}
